import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import React, { createContext, PureComponent } from 'react';
import configureAnalyticsScripts from './scripts';
import { track } from './helpers';

export const AnalyticsContext = createContext('analytics');

/**
 * A React Component
 * @typedef {Function} Component
 */

const DEFAULT_DEBOUNCE_WAIT = 200;

const queue = {
  users: [],
  events: [],
  conversions: []
};

/**
 * The AnalyticsProvider is a React component that leverages the [Context API](https://reactjs.org/docs/context.html)
 * to provide tracking methods to any component that needs to log analytics data on
 * user identification, user conversion or general events.
 * It is meant to support a variety of analytics clients (google|bing|facebook|kinesis|kissmetrics)
 * that are each unique in how they are downloaded and attached to the DOM and used,
 * but this component unifies working with those clients into tracking methods that work for all clients.
 *
 * @kind class
 * @name AnalyticsProvider
 * @param {Boolean} [debug=false] Whether or not to debug to the console each event handled by the provider
 * @param {Boolean} [noPublish=false] Whether or not to kick the event through to the analytics library (only set this when you're debugging and don't want to send the events to their corresponding remote service)
 * @param {Number} [debounceWait=200] How long the debouncing window should stay open before letting the tracking method handle the event
 * @param {Component} children Any React components to render beneath the provider (ideally this would be high up in the tree, so most of your app will go here)
 * @param {Object<string, any>} configs This configures the individual analytics clients and will vary based on the client. Generally each should have an `id` or a `url` (for our own analytics services)
 * @returns {Component} The Analytics Provider instance
 */
class AnalyticsProvider extends PureComponent {
  /* eslint-disable react/no-unused-state */
  state = {
    trackUser(user) {
      queue.users.push(user);
    },
    trackEvent(...eventArgs) {
      queue.events.push(eventArgs);
    },
    trackConversion(...conversionArgs) {
      queue.conversions.push(conversionArgs);
    },
    trackEventAsync() {}
  }
  /* eslint-enable react/no-unused-state */

  async componentDidMount() {
    const { configs = {}, debug, noPublish, debounceWait } = this.props;
    const analyticsClients = configureAnalyticsScripts(configs, debug, noPublish);

    const trackers = {
      trackUser(user) {
        return track('identity', user, {}, analyticsClients);
      },
      trackEvent(eventName, eventData, onlyToTheseClients) {
        return track('event', eventName, eventData, analyticsClients, onlyToTheseClients);
      },
      trackConversion(eventName, conversionEvent, onlyToTheseClients) {
        return track('conversion', eventName, conversionEvent, analyticsClients, onlyToTheseClients);
      },
      trackEventAsync(eventName, eventData, onlyToTheseClients) {
        return track('eventAsync', eventName, eventData, analyticsClients, onlyToTheseClients);
      }
    };

    /**
     * It's possible for the tracking methods to be used in one or more
     * component's life-cycle events, which will fire before all the analytics
     * client libraries are downloaded and attached via <script> tags onto the DOM.
     * So we can stub those tracking methods until the AnalyticsProvider has mounted
     * to create a local queue (that we'll clear afterwards) and invoke the
     * tracking methods with any events collected before the component mounts.
     * The setup of the analytics scripts is still very fast (enough that a user
     * can't click anything fast enough to be missed), just not fast enough to
     * catch any invoking of tracking methods inside component mounting lifecycle events
     */
    if (queue.users.length) {
      queue.users.forEach(user => trackers.trackUser(user));
    }
    if (queue.events.length) {
      queue.events.forEach(eventArgs => trackers.trackEvent(...eventArgs));
    }
    if (queue.conversions.length) {
      queue.conversions.forEach(conversionArgs => trackers.trackConversion(...conversionArgs));
    }

    if (debounceWait) {
      Object.keys(trackers).forEach(trackingMethod => {
        if (!trackingMethod.includes('Async')) {
          trackers[trackingMethod] = debounce(trackers[trackingMethod], debounceWait);
        }
      });
    }

    this.setState(trackers);
  }

  render() {
    const { children } = this.props;
    return (
      <AnalyticsContext.Provider value={this.state}>
        {children}
      </AnalyticsContext.Provider>
    );
  }
}

AnalyticsProvider.propTypes = {
  debug: PropTypes.bool,
  noPublish: PropTypes.bool,
  debounceWait: PropTypes.number,
  children: PropTypes.node.isRequired,
  configs: PropTypes.shape({}).isRequired
};

AnalyticsProvider.defaultProps = {
  debounceWait: DEFAULT_DEBOUNCE_WAIT,
  noPublish: false,
  debug: false
};

export default AnalyticsProvider;
